import { RefObject, useEffect } from 'react';
import noop from 'lodash/noop';
import { createFilter } from 'react-select';
import useMenuHandlers from './useMenuHandlers';
import useFormatOptionLabel from './useFormatOptionLabel';
import useSelectValue from './useSelectValue';
import useSelectComponents from './useSelectComponents';
import { TSelectProps, TSelectCommonProps } from '../interfaces';
import { customMobileStyles, customStyles, customStylesSlim, selectTheme, sizeConfig } from '../selectConfig';
import { groupedStylesConfig } from '../groupedSelectConfig';
import { isScreenS } from '@creditflowresearch/uilib/styles/screen/screen';

const baseFilterOption = createFilter({ ignoreCase: true, stringify: (option) => option.label.toString() });

const useSelect = (
  selectProps: TSelectProps,
  selectWrapperRef: RefObject<HTMLDivElement>,
): TSelectCommonProps => {
  const {
    name = 'select',
    isSimpleValue,
    onChange,
    isClearable = true,
    backspaceRemovesValue = true,
    maxMenuHeight = 200,
    onBlur = noop,
    isSlim,
    size,
    isGrouped,
    selectStyles,
    noOptionsMessage = () => 'No results found',
    onDidMount,
    handleFilterOption,
    showClearIndicator,
    ...otherProps
  } = selectProps;

  const menuHandlers = useMenuHandlers(selectProps);
  const isMobile = isScreenS();

  const customSelectStyles = {
    ...(isSlim ? customStylesSlim : customStyles),
    ...(isGrouped ? groupedStylesConfig : {}),
    ...(isMobile && !otherProps.isMulti ? customMobileStyles : {}),
    ...selectStyles,
  };

  // `size`, when set, takes precedence over `isSlim`/defaults. We compose onto the
  // resolved style functions so the base control/value-container styling is kept.
  const sizedSelectStyles = size
    ? {
      ...customSelectStyles,
      control: (base, state) => ({
        ...(customSelectStyles.control?.(base, state) ?? base),
        minHeight: sizeConfig[size].minHeight,
        height: 'auto',
      }),
      // Use the `padding` shorthand (not paddingLeft/Right longhands) so the outer
      // value-container wrapper's `padding: 0` still cancels it — keeping the size
      // padding on a single layer (the inner container), like the default 10px.
      valueContainer: (base) => ({
        ...(customSelectStyles.valueContainer?.(base) ?? base),
        padding: `0 ${ sizeConfig[size].padX }px`,
      }),
      // Constrain the input height so the content doesn't push the control past
      // its `minHeight` floor (see sizeConfig). null => leave the base height.
      input: (base) => ({
        ...(customSelectStyles.input?.(base) ?? base),
        ...(sizeConfig[size].inputHeight != null ? { height: sizeConfig[size].inputHeight } : {}),
      }),
    }
    : customSelectStyles;

  const onSelectChange = (value, meta) => {
    if (isSimpleValue) {
      onChange(value?.value || null);
    } else {
      onChange(value, meta);
    }
  };

  useEffect(() => {
    if (onDidMount) {
      onDidMount();
    }
  }, []);

  return {
    name,
    filterOption: handleFilterOption ?? baseFilterOption,
    onChange: onSelectChange,
    isClearable,
    theme: selectTheme,
    styles: sizedSelectStyles,
    noOptionsMessage,
    backspaceRemovesValue,
    maxMenuHeight,
    onBlur: () => onBlur(),
    ...otherProps,
    ...menuHandlers,
    ...useFormatOptionLabel(selectProps, menuHandlers.menuIsOpen),
    ...useSelectValue(selectProps),
    ...useSelectComponents(selectProps, selectWrapperRef, menuHandlers.menuIsOpen, otherProps.isMulti),
  };
};

export default useSelect;
